import React, { useState, useEffect, useMemo } from "react";
import { Formik } from "formik";
import { isEqual } from "lodash";
import { useCitiesUIContext } from "../CitiesUIContext";
import * as countryCrud from "../../../_redux/countries/countriesCrud";
import * as stateCrud from "../../../_redux/states/statesCrud";

const prepareFilter = (queryParams, values) => {
  const { country_id, state_id, searchText } = values;
  const newQueryParams = { ...queryParams };
  const filter = {};

  if (country_id && country_id !== "") {
    filter.country_id = country_id;
  }
  if (state_id && state_id !== "") {
    filter.state_id = state_id;
  }
  if (searchText && searchText.trim() !== "") {
    filter.name = searchText.trim();
  }

  newQueryParams.filter = filter;
  return newQueryParams;
};

export function CitiesFilter({ listLoading }) {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);

  // Cities UI Context
  const citiesUIContext = useCitiesUIContext();
  const citiesUIProps = useMemo(() => {
    return {
      setQueryParams: citiesUIContext.setQueryParams,
      queryParams: citiesUIContext.queryParams,
    };
  }, [citiesUIContext]);

  // Load countries on mount
  useEffect(() => {
    countryCrud.findCountries({ pageSize: 1000, pageNumber: 1, sortField: 'name', sortOrder: 'ASC' }).then(res => {
      if (res.data && res.data.entities) {
        setCountries(res.data.entities);
      }
    }).catch(err => console.error("Error fetching countries", err));
  }, []);

  // Load states if country_id is already in queryParams
  useEffect(() => {
    const activeCountryId = citiesUIProps.queryParams.filter?.country_id;
    if (activeCountryId) {
      stateCrud.findStates({ filter: { country_id: activeCountryId }, pageSize: 1000, pageNumber: 1, sortField: 'name', sortOrder: 'ASC' }).then(res => {
        if (res.data && res.data.entities) {
          setStates(res.data.entities);
        }
      }).catch(err => console.error("Error fetching states", err));
    } else {
      setStates([]);
    }
  }, [citiesUIProps.queryParams.filter?.country_id]);

  const applyFilter = (values) => {
    const newQueryParams = prepareFilter(citiesUIProps.queryParams, values);
    if (!isEqual(newQueryParams, citiesUIProps.queryParams)) {
      newQueryParams.pageNumber = 1;
      citiesUIProps.setQueryParams(newQueryParams);
    }
  };

  const handleCountryChange = (countryVal, values, setFieldValue) => {
    setFieldValue("country_id", countryVal);
    setFieldValue("state_id", "");
    if (countryVal) {
      stateCrud.findStates({ filter: { country_id: countryVal }, pageSize: 1000, pageNumber: 1, sortField: 'name', sortOrder: 'ASC' }).then(res => {
        if (res.data && res.data.entities) {
          setStates(res.data.entities);
        }
      }).catch(err => console.error("Error fetching states", err));
    } else {
      setStates([]);
    }
    applyFilter({ ...values, country_id: countryVal, state_id: "" });
  };

  return (
    <>
      <Formik
        initialValues={{
          country_id: citiesUIProps.queryParams.filter?.country_id || "",
          state_id: citiesUIProps.queryParams.filter?.state_id || "",
          searchText: citiesUIProps.queryParams.filter?.name || "",
        }}
        enableReinitialize={true}
        onSubmit={(values) => {
          applyFilter(values);
        }}
      >
        {({
          values,
          handleSubmit,
          handleBlur,
          setFieldValue,
          resetForm,
        }) => (
          <form onSubmit={handleSubmit} className="form form-label-right mb-4">
            <div className="form-group row align-items-center">
              <div className="col-lg-3 col-md-4 mb-2">
                <select
                  className="form-control"
                  name="country_id"
                  value={values.country_id}
                  onChange={(e) => handleCountryChange(e.target.value, values, setFieldValue)}
                >
                  <option value="">All Countries</option>
                  {countries.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <small className="form-text text-muted">
                  Filter by <b>Country</b>
                </small>
              </div>

              <div className="col-lg-3 col-md-4 mb-2">
                <select
                  className="form-control"
                  name="state_id"
                  value={values.state_id}
                  disabled={!values.country_id}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFieldValue("state_id", val);
                    applyFilter({ ...values, state_id: val });
                  }}
                >
                  <option value="">{values.country_id ? "All States" : "Select Country First"}</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <small className="form-text text-muted">
                  Filter by <b>State</b>
                </small>
              </div>

              <div className="col-lg-3 col-md-4 mb-2">
                <input
                  type="text"
                  className="form-control"
                  name="searchText"
                  placeholder="Search city name..."
                  onBlur={handleBlur}
                  value={values.searchText}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFieldValue("searchText", val);
                    applyFilter({ ...values, searchText: val });
                  }}
                />
                <small className="form-text text-muted">
                  <b>Search</b> by name
                </small>
              </div>

              {(values.country_id || values.state_id || values.searchText) && (
                <div className="col-lg-2 col-md-4 mb-2">
                  <button
                    type="button"
                    className="btn btn-light-danger btn-sm"
                    onClick={() => {
                      setStates([]);
                      resetForm({ values: { country_id: "", state_id: "", searchText: "" } });
                      applyFilter({ country_id: "", state_id: "", searchText: "" });
                    }}
                  >
                    <i className="fa fa-times mr-1"></i> Clear Filters
                  </button>
                </div>
              )}
            </div>
          </form>
        )}
      </Formik>
    </>
  );
}

